import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Navigate, Link } from "react-router-dom";
import { useAuthStore } from "../store/authStore";

const styles = [
  "ghibli",
  "anime",
  "claymation",
  "puppet",
  "pixel",
  "pixar",
  "disney",
  "fantasy",
  "lego",
];

const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [error, setError] = useState<string | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [buttonContainerTop, setButtonContainerTop] = useState(0);
  const [stickyHeight, setStickyHeight] = useState(0);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const textContentRef = useRef<HTMLDivElement>(null); // Ref for the parent container to add padding

  const { isAuthenticated, signInAnonymously, isLoading } = useAuthStore();

  // Effect to get the initial top offset of the button container
  useEffect(() => {
    if (buttonContainerRef.current) {
      // Calculate offset relative to the document body
      const rect = buttonContainerRef.current.getBoundingClientRect();
      setButtonContainerTop(rect.top + window.scrollY);
    }
  }, []); // Empty dependency array ensures this runs only once after mount

  // Effect to handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      if (!buttonContainerRef.current) return;

      const currentScrollY = window.scrollY;
      // Use a small buffer (e.g., 1px) to prevent flickering if exactly at the edge
      const shouldBeSticky = currentScrollY > buttonContainerTop - 1;

      if (shouldBeSticky !== isSticky) {
        setIsSticky(shouldBeSticky);
        if (shouldBeSticky) {
          // Measure height when it becomes sticky
          setStickyHeight(buttonContainerRef.current.offsetHeight);
        } else {
          setStickyHeight(0); // Reset height when not sticky
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isSticky, buttonContainerTop]); // Re-run effect if isSticky or buttonContainerTop changes

  // Effect to apply padding to the parent container
  useEffect(() => {
    if (textContentRef.current) {
      // Ensure smooth transition for padding change
      textContentRef.current.style.transition = "padding-top 0.2s ease";
      textContentRef.current.style.paddingTop = isSticky
        ? `${stickyHeight}px`
        : "0px";
    }
  }, [isSticky, stickyHeight]);

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const isDeLocale = currentLanguage === "de";
  const basePath = isDeLocale ? "/images/de/" : "/previews/";
  const originalImageName = isDeLocale
    ? "female_25_original.jpg"
    : "thai_female.jpeg";
  const afterStyle = "ghibli";
  const afterImageName = isDeLocale
    ? `female_25_${afterStyle}.jpg`
    : `thai_female_${afterStyle}.jpeg`;
  const styleImagePrefix = isDeLocale ? "female_25_" : "thai_female_";
  const styleImageSuffix = isDeLocale ? ".jpg" : ".jpeg";

  return (
    <div className="flex flex-col min-h-screen bg-black text-white">
      {/* Using the shared Header component */}
      <Header />

      {/* Main Content Section - Centered */}
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row items-center">
          {/* Text Content - Made more responsive */}
          <div
            ref={textContentRef}
            className="w-full md:w-1/2 text-center md:text-left mb-8 md:mb-0"
          >
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              {t("landingPage.headline")}
            </h1>
            <p className="text-lg text-gray-300 mb-8 mx-auto md:mx-0 max-w-xl">
              {t("landingPage.description")}
            </p>
            {/* Prominent Try Anonymously Button */}
            <div
              ref={buttonContainerRef} // Add ref here
              className={`text-center md:text-left mt-4 mb-8 transition-all duration-200 ${
                isSticky
                  ? "fixed top-0 left-0 right-0 z-50 bg-black bg-opacity-90 shadow-xl py-4 flex justify-center items-center"
                  : "relative" // Ensure it's relative when not sticky
              }`}
            >
              {" "}
              {/* Added container for alignment */}
              <button
                className={`btn rounded-md py-4 px-8 text-xl font-bold text-white transition-all duration-200 inline-block shadow-lg hover:shadow-xl ${
                  isLoading ? "opacity-70 cursor-not-allowed" : ""
                } ${isSticky ? "mx-auto" : ""}`} // Center button if sticky
                onClick={handleAnonymousSignIn}
                disabled={isLoading}
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  padding: "1rem 2rem",
                  backgroundImage:
                    "linear-gradient(270deg, #3b82f6, #2563eb, #0ea5e9)",
                  backgroundSize: "200% 200%",
                  backgroundPosition: "0% 50%",
                  animation: "gradientShift 8s ease infinite",
                }}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("common.loading", "Loading...")}
                  </span>
                ) : (
                  t("landingPage.tryAnonymously", "Try it out for free!")
                )}
              </button>
            </div>
            {/* Login Link */}
            <div className="text-center md:text-left mt-4">
              <p className="text-sm text-gray-400">
                {t("landingPage.loginPrompt", "Already have an account?")}{" "}
                <Link
                  to="/login"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  {t("landingPage.loginLink", "Log in")}
                </Link>
              </p>
            </div>
          </div>

          {/* Before-After Image Display */}
          <div className="w-full md:mt-0 md:ml-8 md:w-1/2">
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              {/* Before Image */}
              <div className="flex flex-col items-center">
                <p className="text-center font-semibold mb-2 text-gray-300">
                  {t("landingPage.imageCompare.before")}
                </p>
                <img
                  src={`${basePath}${originalImageName}`}
                  alt={t("landingPage.imageCompare.before")}
                  className="rounded-lg shadow-xl object-cover w-full max-w-xs md:max-w-none md:h-auto"
                  style={{ maxHeight: "60vh" }}
                />
              </div>
              {/* After Image */}
              <div className="flex flex-col items-center">
                <p className="text-center font-semibold mb-2 text-gray-300">
                  {t("landingPage.imageCompare.after")}
                </p>
                <img
                  src={`${basePath}${afterImageName}`}
                  alt={t("landingPage.imageCompare.after")}
                  className="rounded-lg shadow-xl object-cover w-full max-w-xs md:max-w-none md:h-auto"
                  style={{ maxHeight: "60vh" }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* <div className="container mx-auto px-4 py-8">
          <LoginForm />
        </div> */}

        {/* Style Gallery Section - Apple Marquee Style */}
        <div className="container mx-auto px-4 py-12 md:py-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-10">
            {t("landingPage.styleGallery.title", "Explore Styles")}
          </h2>
          <div className="relative w-full overflow-hidden group">
            {/* Left Fade */}
            <div className="absolute top-0 left-0 bottom-0 z-10 w-16 md:w-24 bg-gradient-to-r from-black to-transparent pointer-events-none"></div>
            {/* Right Fade */}
            <div className="absolute top-0 right-0 bottom-0 z-10 w-16 md:w-24 bg-gradient-to-l from-black to-transparent pointer-events-none"></div>

            <div className="flex animate-marquee group-hover:pause-animation">
              {/* Render styles twice for seamless loop */}
              {[...styles, ...styles].map((style, index) => (
                <div
                  key={`${style}-${index}`} // Ensure unique keys for duplicated items
                  className="flex-shrink-0 mx-4 md:mx-6 w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48" // Fixed size for items
                >
                  <img
                    src={`${basePath}${styleImagePrefix}${style}${styleImageSuffix}`}
                    alt={style}
                    className="rounded-lg shadow-xl object-cover w-full h-full aspect-square transition-transform duration-200 ease-in-out hover:scale-105"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Using the shared Footer component */}
      <Footer />

      {/* Inline styles for the marquee animation */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          } /* Translate by half the total width */
        }
        .animate-marquee {
          display: flex;
          animation: marquee 40s linear infinite; /* Adjust duration as needed */
          will-change: transform; /* Optimize animation performance */
        }
        .pause-animation:hover {
          animation-play-state: paused;
        }
      `}</style>
      {/* Display error message if any */}
      {error && <p className="text-red-500 text-center py-2">{t(error)}</p>}
    </div>
  );

  async function handleAnonymousSignIn() {
    setError(null);
    try {
      await signInAnonymously();
    } catch (err) {
      setError(err instanceof Error ? err.message : "auth.errors.unknown");
    }
  }
};

export default LandingPage;
